import {
  getCurrentPositionAsync,
  getForegroundPermissionsAsync,
  getLastKnownPositionAsync,
  LocationAccuracy,
} from 'expo-location';
import { useContext, useEffect, useState } from 'react';
import FetchUser from 'services/graphql/queries/FetchUser';
import { GeocodeDetailsResponse } from 'shared/components/maps/PredictionMapView/PredictionMapView.helpers';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import fetchAddressCoords from 'shared/utils/maps/fetchAddressCoords';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useQuery } from 'urql';

const LOCATION_ACCURACY = LocationAccuracy.Balanced;

interface UseRewardsLocationResult {
  coords: {
    latitude: number;
    longitude: number;
  } | null;
  isLocationDenied: boolean;
  isLocationFetching: boolean;
}

export const useRewardsLocation = (): UseRewardsLocationResult => {
  const { currentEnv, getMapsUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const mapsUrl = getMapsUrl(currentEnv);

  const [coords, setCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [isLocationDenied, setIsLocationDenied] = useState(false);
  const [isLocationFetching, setIsLocationFetching] = useState(true);

  const [{ data: userData, fetching: userFetching }] = useQuery({
    query: FetchUser,
  });

  useEffect(() => {
    const abortController = new AbortController();
    const requestLocationPermission = async (): Promise<void> => {
      try {
        const { status } = await getForegroundPermissionsAsync();
        if (status !== 'granted') {
          if (!abortController.signal.aborted) {
            setIsLocationDenied(true);
            setIsLocationFetching(false);
          }
          return;
        }
        const position = await getLastKnownPositionAsync();

        if (position && !abortController.signal.aborted) {
          setCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setIsLocationFetching(false);
        }

        const freshPosition = await getCurrentPositionAsync({
          accuracy: LOCATION_ACCURACY,
        });

        if (!abortController.signal.aborted) {
          setCoords({
            latitude: freshPosition.coords.latitude,
            longitude: freshPosition.coords.longitude,
          });
          setIsLocationFetching(false);
        }
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to fetch location');
        logErrorWithContext(error, {
          featureArea: FeatureArea.REWARDS,
          responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
          severity: ErrorSeverity.LOW,
        });
        if (!abortController.signal.aborted) {
          setIsLocationDenied(true);
          setIsLocationFetching(false);
        }
      }
    };

    requestLocationPermission();
    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (!isLocationDenied || userFetching || !userData || coords) {
      return;
    }
    const abortController = new AbortController();

    const fetchCoordinatesFromZip = async (zip: string): Promise<void> => {
      try {
        const response = await fetchAddressCoords(zip, mapsUrl);
        const result = (await response.json()) as GeocodeDetailsResponse;
        if (result?.results?.[0]?.geometry?.location) {
          const location = {
            lat: parseFloat(result.results[0].geometry.location.lat),
            lng: parseFloat(result.results[0].geometry.location.lng),
          };
          setCoords({
            latitude: location.lat,
            longitude: location.lng,
          });
        }
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch address coordinates');
        logErrorWithContext(error, {
          featureArea: FeatureArea.REWARDS,
          responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
          severity: ErrorSeverity.MEDIUM,
        });
      }
    };

    const zip = userData.user?.customerInfo?.address?.zip;
    if (zip) {
      fetchCoordinatesFromZip(zip);
    } else {
      logErrorWithContext('Zip code is null or empty', {
        featureArea: FeatureArea.REWARDS,
        responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
        severity: ErrorSeverity.MEDIUM,
      });
    }

    return () => {
      abortController.abort();
    };
  }, [isLocationDenied, userData, userFetching, mapsUrl, coords]);

  return {
    coords,
    isLocationDenied,
    isLocationFetching,
  };
};

export default useRewardsLocation;
